<!--
AddDirectPurchase Component - With FOC Reset Functionality
==========================================================

Features:
1. Global FOC Toggle: Always visible below BRAND selection
   - No conditions - displays even when no products are searched
   - When toggled, resets ALL selected items if any exist
   - Shows warning dialog before resetting items
   - Applies to all new products added after setting

2. Reset Functionality:
   - If FOC is toggled and items exist: Shows warning dialog
   - User can confirm to reset all items or cancel to keep items
   - If cancelled, FOC checkbox reverts to previous state
   - If no items exist, FOC toggles without warning

3. Smart Packaging Selection: Each product row has dynamic packaging dropdown
   - "Single Item" only shows if:
     * product.original_price_unit is not present, OR
     * product.original_price_unit equals 'single_item'
   - Otherwise, "Single Item" is removed from dropdown options
   - Additional options based on available pricing:
     * box: Available if product.packaging.box.price exists
     * card: Available if product.packaging.card.price exists
     * pill: Available if product.packaging.pill.price exists
   - Default selection logic:
     * Uses product.original_price_unit as default if it exists in available options
     * Falls back to first available option if original_price_unit is not available
   - Enabled only when product has FOC=true

API Changes:
- Each product item includes:
  - foc: boolean (true/false based on global FOC setting when added)
  - packaging_selection: string (box/card/pill/single_item, user-selectable per product)

Maintenance Notes:
- FOC toggle is global and affects all existing and future items
- Warning dialog prevents accidental data loss
- Clean reset functionality clears products, current product, and search data
-->
<template>
	<div class="mainWrapper direct-purchase">
		<h1>Create Direct Purchase</h1>
		<div class="form" v-if="brands.length > 0">
			<div>
				<span class="label">BRAND</span>
				<b-autocomplete
					v-model="brandSuggestions"
					:data="filteredBrands"
					:open-on-focus="true"
					placeholder="Brand"
					field="name"
					@select="option => (selectedBrand = option)"
					class="brandAutocomplete"
					:disabled="products.length > 0"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</div>

			<!-- Global FOC Checkbox - Always visible -->

			<div v-if="selectedBrand">
				<span class="label">BD CODE</span>
				<div class="addDetails">
					<b-autocomplete
						:data="searchData"
						placeholder="BD Code"
						field="bd_code"
						@typing="searchProducts"
						@select="option => (product = option)"
						class="brandAutocomplete"
					>
						<template slot="empty">No results found</template>
					</b-autocomplete>
				</div>
			</div>

			<div v-if="product">
				<span class="label">ADD</span>
				<b-button type="is-info" icon-left="plus-circle-outline" @click="submit">ADD PRODUCT</b-button>
			</div>
		</div>

		<div class="form">
			<div class="foc-section">
				<span class="label">FOC (FREE OF CHARGE)</span>
				<b-field>
					<b-checkbox v-model="globalFOC" @input="onFOCToggle">
						Enable FOC
					</b-checkbox>
				</b-field>
			</div>
		</div>

		<div class="list">
			<!-- v-if="products.length > 0" -->
			<div class="products-list header">
				<div>
					BD CODE
				</div>

				<div>
					BARCODE
				</div>

				<div>
					NAME
				</div>

				<div>
					FOC
				</div>

				<div>
					PACKAGING
				</div>

				<div>
					RECEIVED DATE
				</div>

				<div>
					QUANTITY
				</div>

				<!-- <div>
					PRICE
				</div> -->

				<div>
					REMARK
				</div>
			</div>

			<div v-for="(item, index) of products" v-bind:key="index" class="products-list">
				<div>
					{{ item.bd_code }}
				</div>

				<div>
					{{ item.barcode }}
				</div>

				<div>
					{{ item.name }}
				</div>

				<div>
					<b-checkbox v-model="item.foc" disabled>FOC</b-checkbox>
				</div>

				<div>
					<b-select
						v-model="item.packaging_selection"
						:disabled="!item.foc || (item.availablePackagingOptions && item.availablePackagingOptions.length <= 1)"
						@input="updatePackagingSelection(index, $event)"
					>
						<option
							v-for="option in item.availablePackagingOptions || [{ value: 'single_item', label: 'Single Item' }]"
							:key="option.value"
							:value="option.value"
						>
							{{ option.label }}
						</option>
					</b-select>
				</div>

				<div>
					<b-datepicker :date-formatter="dateFormatter" v-model="item.received_date" icon="calendar-today"> </b-datepicker>
				</div>

				<div>
					<input name="qtyInput" class="qtyInput" type="number" v-on:change="update(index, 'qty', $event.target.value)" />
				</div>

				<!-- <div>
					<input
						name="priceInput"
						class="priceInput"
						type="number"
						v-on:change="update(index, 'price', $event.target.value)"
					/>
				</div> -->

				<div>
					<textarea name="remark" class="remark" placeholder="Remark" v-model="item.remark" />
				</div>
			</div>
		</div>

		<div v-if="products.length > 0" class="detail-buttons-wrapper">
			<div></div>
			<div class="align-right">
				<button class="big-button" @click="save()">SAVE</button>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'AddDirectPurchase',
	data: function() {
		return {
			warehouse: null,
			brands: [], // List of brands from API
			selectedBrand: null, // Selected brand
			bdcodeData: [], // API bdcode data
			searchData: [], // API Search data
			brandSuggestions: '', // List of brands
			product: null, // 1 selected product
			products: [], // Selected products
			name: null,
			supplier: null,
			// FOC checkbox state - applies to all new products added
			globalFOC: false
		}
	},
	computed: {
		filteredBrands() {
			return this.brands.filter(option => {
				return (
					option.name
						.toString()
						.toLowerCase()
						.indexOf(this.brandSuggestions.toLowerCase()) >= 0
				)
			})
		}
	},

	watch: {
		product: function(newData) {
			console.log(newData)
		},

		selectedBrand: function(newData) {
			this.product = null
			this.products = []
			// Don't reset globalFOC when brand changes - user might want to keep FOC setting
		}
	},

	mounted: async function() {
		// Get list of brands
		const data = {
			populate: ['suppliers']
		}

		const apiGetWarehouse = await this.$api.getWarehouse({ data })

		this.supplier = apiGetWarehouse.data.suppliers[0]._id

		// Get brands name which are related to this warehouse
		await Promise.all(
			apiGetWarehouse.data.suppliers[0].brand.map(async brand => {
				const brandData = {
					id: brand
				}

				const apiGetBrand = await this.$api.getBrand({ data: brandData })

				if (apiGetBrand !== undefined) {
					this.brands.push(apiGetBrand.data.list[0])
				}
			})
		)
	},

	methods: {
		save: async function() {
			var newData = []

			this.products.map(product => {
				var productData = {}
				productData.product = product._id
				productData.received_date = this.$moment(product.received_date).format('DD/MM/YYYY')
				productData.supplier = this.supplier
				// productData.price = product.price;
				productData.currency = 'MMK'
				productData.qty = product.qty
				productData.remark = product.remark

				// Add FOC flag - true if FOC is enabled, false otherwise
				productData.foc = product.foc || false

				// Add packaging selection for all items
				productData.packaging_selection = product.packaging_selection || 'single_item'

				newData.push(productData)
			})

			const data = {
				// we dont need to pass warehouse as it will get from manager role
				direct_purchase_items: newData
			}

			const apiSaveDP = await this.$api.saveDP({ data })

			if (apiSaveDP !== undefined) {
				this.$swal.fire(apiSaveDP.data.direct_purchase.code)

				this.$router.push('/direct-purchase')
			}
		},

		dateFormatter(dt) {
			return dt.toLocaleDateString('en-GB')
		},

		submit: async function() {
			// Initialize product properties
			this.product.received_date = null
			this.product.qty = null
			this.product.remark = null

			// Set FOC status from global checkbox
			this.product.foc = this.globalFOC

			// Calculate available packaging options for this product
			this.product.availablePackagingOptions = this.calculateProductPackagingOptions(this.product)

			// Set default packaging selection based on product's original_price_unit or first available option
			this.product.packaging_selection = this.getDefaultPackagingSelection(this.product, this.product.availablePackagingOptions)

			this.products.push(this.product)

			// Reset form after adding product (don't reset globalFOC)
			this.product = null
			this.searchData = []
		},

		update: function(index, item, value) {
			console.log(index, item, value)

			if (item == 'qty') {
				this.products[index].qty = Number(value)
			}

			// if (item == "price") {
			// 	this.products[index].price = Number(value);
			// }
		},

		// Autocomplete search
		searchProducts: async function(name) {
			if (name.length > 1) {
				const data = {
					brand: this.selectedBrand._id,
					bd_code: name
				}

				const apiSearchProduct = await this.$api.searchProduct({ data })

				this.searchData = apiSearchProduct.data.list

				// If no data found, try to search with barcode
				if (apiSearchProduct.data.list && apiSearchProduct.data.list.length < 1) {
					delete data.bd_code

					data.barcode = name

					const apiSearchProduct = await this.$api.searchProduct({ data })

					this.searchData = apiSearchProduct.data.list
				}
			}
		},

		// Handle packaging selection change for individual products
		updatePackagingSelection: function(index, value) {
			this.products[index].packaging_selection = value
		},

		// Calculate available packaging options for a specific product
		calculateProductPackagingOptions: function(product) {
			// Start with empty options array
			const options = []

			try {
				// Check if we should include "Single Item" option
				// Only include if original_price_unit is not present or equals 'single_item'
				if (!product.original_price_unit || product.original_price_unit === 'single_item') {
					options.push({ value: 'single_item', label: 'Single Item' })
				}

				// Check if packaging pricing is available and add valid options
				if (product && product.packaging) {
					// Check for box pricing
					if (product.packaging.box && product.packaging.box.price) {
						options.push({ value: 'box', label: 'Box' })
					}

					// Check for card pricing
					if (product.packaging.card && product.packaging.card.price) {
						options.push({ value: 'card', label: 'Card' })
					}

					// Check for pill pricing
					if (product.packaging.pill && product.packaging.pill.price) {
						options.push({ value: 'pill', label: 'Pill' })
					}
				}

				// If no options available at all, fallback to single_item to prevent empty dropdown
				if (options.length === 0) {
					options.push({ value: 'single_item', label: 'Single Item' })
				}
			} catch (error) {
				console.warn('Error calculating packaging options:', error)
				// Fallback to single_item only if there's an error
				options.push({ value: 'single_item', label: 'Single Item' })
			}

			return options
		},

		// Get the default packaging selection based on product's original_price_unit
		getDefaultPackagingSelection: function(product, availableOptions) {
			// If product has original_price_unit, try to use it as default
			if (product.original_price_unit) {
				// Check if the original_price_unit is available in the options
				const matchingOption = availableOptions.find(option => option.value === product.original_price_unit)
				if (matchingOption) {
					return product.original_price_unit
				}
			}

			// Fallback to first available option
			if (availableOptions && availableOptions.length > 0) {
				return availableOptions[0].value
			}

			// Final fallback
			return 'single_item'
		},

		// Handle FOC checkbox toggle - reset all items with warning if items exist
		onFOCToggle: async function(value) {
			// If there are already products in the list, show warning
			if (this.products.length > 0) {
				const result = await this.$swal.fire({
					title: 'Warning!',
					text: 'Changing FOC setting will reset all selected items. Do you want to continue?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#d33',
					cancelButtonColor: '#3085d6',
					confirmButtonText: 'Yes, reset all items',
					cancelButtonText: 'Cancel'
				})

				if (result.value) {
					this.resetAllProducts()
					this.$swal.fire('Reset!', 'All items have been cleared.', 'success')
				} else {
					// User cancelled - revert the checkbox to previous state
					console.log('User cancelled, reverting checkbox...')
					this.$nextTick(() => {
						this.globalFOC = !value
					})
				}
			}
			// If no products exist, just change the FOC setting without warning
		},

		// Reset all selected products
		resetAllProducts: function() {
			// Simple direct assignment - should trigger reactivity
			this.products = []
			this.product = null
			this.searchData = []

			// Force DOM update in case of reactivity issues
			this.$nextTick(() => {
				console.log('Next tick - Products length:', this.products.length)
			})
		}
	}
}
</script>

<style scoped>
@import '../styles/AddDirectPurchase.css';
</style>
